<template>
  <div class="rates-page">
    <Promo id="rates" :type="PromoType.Page" code="rates" />

    <div class="group-section">
      <section id="section_consultation" class="rates-page__tariffs" data-section-num="2">
        <div class="container-2024 grid-2024">
          <header class="rates-page__header g-col-full g-start-lg-4 g-col-lg-6">
            <h2 v-if="title" class="rates-page__title h2" v-html="title" />
            <CmsContent
              v-if="description"
              class="rates-page__intro t2"
              :html="description"
            />
          </header>

          <div class="rates-page__schedule g-col-full">
            <div class="rates-page__schedule-header-viewport">
              <div ref="scheduleHeader" class="rates-page__schedule-header">
                <div class="rates-page__schedule-spacer" />
                <article
                  v-for="tariff in tariffs"
                  :key="tariff.CODE"
                  class="rates-page__card"
                >
                  <div class="rates-page__card-header">
                    <i
                      class="rates-page__card-icon"
                      :class="`-${tariff.CODE.toLowerCase().split('_')[0]}`"
                    />
                    <p class="rates-page__card-title t2" v-html="tariff.NAME" />
                    <span class="rates-page__card-sign t3" v-html="tariff.ASSETS_DESC" />
                  </div>
                  <button
                    type="button"
                    class="rates-page__more rates-page__more--desktop"
                    @click="openTariff(tariff.CODE)"
                  >
                    {{ tariff.BUTTON_MORE }}
                  </button>
                </article>
              </div>
            </div>

            <div class="rates-page__schedule-body-viewport" @scroll="syncScheduleHeader">
              <div class="rates-page__schedule-body">
                <div class="rates-page__schedule-row">
                  <div class="rates-page__row-name t2">Описание</div>
                  <div
                    v-for="tariff in tariffs"
                    :key="tariff.CODE"
                    class="rates-page__cell rates-page__description t2"
                    v-html="tariff.PREVIEW_TEXT"
                  />
                </div>

                <div class="rates-page__schedule-row">
                  <div class="rates-page__row-name t2" v-html="transactionFeeLabel" />
                  <div
                    v-for="tariff in tariffs"
                    :key="tariff.CODE"
                    class="rates-page__cell rates-page__fee t2"
                  >
                    {{ tariff.TRANSACTION_FEE }}
                  </div>
                </div>
              </div>
            </div>

            <div class="rates-page__mobile-tariffs">
              <article
                v-for="tariff in tariffs"
                :id="tariff.CODE"
                :key="tariff.CODE"
                class="rates-page__mobile-card"
              >
                <div class="rates-page__card-header">
                  <i
                    class="rates-page__card-icon"
                    :class="`-${tariff.CODE.toLowerCase().split('_')[0]}`"
                  />
                  <p class="rates-page__card-title t2" v-html="tariff.NAME" />
                  <span class="rates-page__card-sign t3" v-html="tariff.ASSETS_DESC" />
                </div>
                <div
                  v-if="tariff.PREVIEW_TEXT"
                  class="rates-page__cell rates-page__description t2"
                  v-html="tariff.PREVIEW_TEXT"
                />
                <div class="rates-page__cell rates-page__fee t2">
                  <span class="rates-page__fee-label" v-html="transactionFeeLabel" />
                  <span>{{ tariff.TRANSACTION_FEE }}</span>
                </div>
                <button
                  type="button"
                  class="rates-page__more"
                  @click="openTariff(tariff.CODE)"
                >
                  {{ tariff.BUTTON_MORE }}
                </button>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section id="consultation" class="rates-page__form">
        <div class="container-2024 grid-2024">
          <FormPersConsultation
            class="g-col-full g-start-lg-4 g-col-lg-6"
            sid="consultation"
            theme="light"
            title="Нужна помощь<br/>с&nbsp;выбором тарифа?"
            description="Оставьте свои контактные данные — финансовый консультант свяжется с&nbsp;вами, чтобы ответить на&nbsp;ваши вопросы"
            @after-submit="handleFormSubmit"
          />
        </div>
      </section>
    </div>

    <LazyModal
      v-if="isTariffModalOpen"
      class-name="rates-page__details-modal"
      :overlay="false"
      @on-close="closeTariff"
    >
      <TariffModal
        :tariffs="tariffs"
        :active-tariff="activeTariff"
        @select="selectTariff"
        @close="closeTariff"
      />
    </LazyModal>

    <LazyModal
      v-if="isServiceModalOpen"
      view="classic"
      width="md"
      :title="serviceModal.title"
      :overlay="true"
      theme="light"
      @on-close="closeServiceModal"
    >
      <CmsContent
        v-if="serviceModal.description"
        class="t1"
        :html="serviceModal.description"
      />
    </LazyModal>
  </div>
</template>

<script setup lang="ts">
  import { defineAsyncComponent } from '#imports';
  import { Promo, PromoType } from '@nuxt-widgets/promo';
  import FormPersConsultation from '@nuxt-features/form-pers-consultation';
  import { CmsContent } from '@nuxt-shared/ui/cms-content';
  import TariffModal from './ui/TariffModal.vue';
  import { useRatesModel } from './model';

  const LazyModal = defineAsyncComponent(() =>
    import('@nuxt-shared/ui/modal').then((module) => module.Modal),
  );

  const transactionFeeLabel =
    'Комиссия за&nbsp;сделку с ценными бумагами <span>Самостоятельная торговля (QUIK, ATON Line)</span>';

  const {
    title,
    description,
    tariffs,
    scheduleHeader,
    activeTariff,
    isTariffModalOpen,
    serviceModal,
    isServiceModalOpen,
    openTariff,
    selectTariff,
    closeTariff,
    syncScheduleHeader,
    handleFormSubmit,
    closeServiceModal,
  } = await useRatesModel();
</script>

<style lang="scss">
  @use './style.scss';
</style>
